import {Platform} from '@ionic/angular';
import * as uuid from 'uuid';
import {environment} from '../../environments/environment';
import {sendLogs} from './send-logs';
import {appendAppLog, readAppLogs, replaceAppLogs} from '../services/app-log.service';


interface Serial {
  requestPermission: ((
    successCallback: ((message: string) => void),
    errorCallback: ((message: string) => void)
  ) => Promise<void> );
  open: ((
    opts: {
      baudRate: number,
    },
    success: ((message: string) => void),
    failure: ((message: string) => void)
  ) => Promise<void>);
  close: ((success: ((message: string) => void), failure: ((message: string) => void)) => Promise<void>);
  update: ((command: string, success: ((message: string) => void), failure: ((message: string) => void)) => Promise<void>);
  updateProgress: ((success: ((message: string) => void), failure: ((message: string) => void)) => Promise<void>);
  write: ((command: string, version: string, id: string, success: ((message: string) => void), failure: ((message: string) => void)) => Promise<void>);
}

export class SerialDispatcher {
  private isEnabled: boolean;
  private serial: Serial;
  private result = '';
  private written = false;
  private isDone = false;
  private success = false;
  private command = '';
  private isInit = false;
  private requestIndex = 0;
  private progress = 0;
  private platform: Platform;
  private readonly params: {vid: string, pid: string, driver: string, baudRate: number};
  // private platform: Platform;
  private version;
  private scannerSerial;
  private blocksRequested = 0;
  private interimResult = '';

  constructor(params: {vid: string, pid: string, driver: string, baudRate: number}, platform: Platform, scannerVersion: any, scannerSerial: any) {
    this.params = params;
    this.isEnabled = false;
    this.platform = platform;
    this.version = (scannerVersion ?? 1).toString();
    this.scannerSerial = scannerSerial ?? '';
    // @ts-ignore
    if (this.platform.is('cordova') && this.platform.is('android')) {
      // @ts-ignore
      this.serial = window.serial;
      this.isEnabled = true;
    }
  }

  pushToLog = (msg: any) => {
    if (!environment.appDesignSettings.logScanner) {
      return;
    }
    let log = readAppLogs('scanner');
    log.push(msg);
    if (log.length > 500) {
      sendLogs(this.scannerSerial ?? 'None entered', log, undefined, undefined, undefined, 'scanner').then((val) => {
        console.log('Logs sent.');
      });
      log = [];
    }
    replaceAppLogs(log, 'scanner');
  }

  onSuccess(result: string) {
    if (result === '_ok' && !this.written) {
      this.written = true;
      return;
    }
    if (result.startsWith('_') && this.version === '2') {
      this.interimResult += result.slice(1);
      return;
    } else if (this.interimResult !== '') {
      result = this.interimResult + result;
    }
    if (result.startsWith(':')) {
      try {
        const json = JSON.parse(result.slice(1));
        if (json.index !== undefined) {
          if (json.index !== this.requestIndex) {
            this.isDone = true;
            this.success = false;
            console.log(`Wrong request index - ${json.index} received, ${this.requestIndex} expected.`);
            return;
          }
        }
        if (json.data !== undefined) {
          this.result = json.data;
        } else if (json.measurement_data !== undefined) {
          this.result = json.measurement_data;
        } else if (json.status !== undefined) {
          this.result = json.status;
        }
      } catch (e) {
        this.isDone = true;
        this.success = false;
        console.log(`Error parsing JSON.`);
        return;
      }
      this.isDone = true;
      this.success = true;
      console.log(`Command ${this.command} - Success: ${this.result}.`);
      return;
    }
    if ((this.command === 'M' || this.command === 'K' || this.command.startsWith('read-flash') || this.command.startsWith('updateData')) && result.length < 10) {
      this.progress = Number(result);
      return;
    }
    if (this.command === 'M' || this.command === 'K' || this.command.startsWith('read-flash')) {
      console.log(`Command ${this.command} - Success: Measurement received`);
    } else {
      console.log(`Command ${this.command} - Success: ${result}.`);
    }
    if (this.command === 'W' || this.command.startsWith('hand')) {
      this.pushToLog({msg: result, timestamp: Math.floor(Date.now() / 1000), type: 'result'});
    }
    this.result = result;
    this.isDone = true;
    this.success = true;
  }

  onFailure(result: string) {
    this.result = result;
    this.isDone = true;
    this.success = false;
    console.log(`Command ${this.command} - Error: ${result}.`);
  }

  getRequestIndex() {
    this.requestIndex += 1;
    if (this.requestIndex >= 1000) {
      this.requestIndex = 1;
    }
    return this.requestIndex;
  }

  getBreakTime() {
    switch (this.command) {
      case 'status':
      case 'status\n':
      case 'get_status':
      case 'get_status\n': return 1000;
      case 'flash_erase\n': return 120000;
      case 'I': return 1000;
      case 'W': return 1000;
      case 'delete': return 10000;
      case 'K':
      case 'M': return 3000;
      case '':
      default: {
        if (this.command.startsWith('measure') || this.command.startsWith('hand') || this.command.startsWith('mode')) {
          return 1500;
        } else if (this.command.startsWith('ap')) {
          return 2000;
        } else if (this.command.startsWith('read-flash')) {
          return 250 + this.blocksRequested * 2;
        } else if (this.command.startsWith('delete')) {
          return 120000;
        }
        return 10;
      }
    }
  }

  async init() {
    this.interimResult = '';
    this.written = false;
    if (!this.isEnabled) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  async requestPermission() {
    await this.init();
    await this.serial.requestPermission((msg: string) => this.onSuccess(msg), (msg: string) => this.onFailure(msg));
    await new Promise((r) => setTimeout(r, 10));
  }

  async open() {
    await this.init();
    await this.serial.open(this.params, (msg: string) => this.onSuccess(msg), (msg: string) => this.onFailure(msg));
    await new Promise((r) => setTimeout(r, 500));
  }

  async close() {
    await this.init();
    await this.serial.close((msg: string) => this.onSuccess(msg), (msg: string) => this.onFailure(msg));
    await new Promise((r) => setTimeout(r, 50));
  }

  async update(command: string)  {
    await this.init();
    this.progress = 0;
    this.command = 'updateData';
    await this.serial.update(command, (msg: string) => this.onSuccess(msg), (msg: string) => this.onFailure(msg));
    await new Promise((r) => setTimeout(r, 1000));
    return;
  }

  async write(command: string, blocksRequested: number = 0)  {
    this.blocksRequested = blocksRequested;
    await this.init();
    this.command = command;
    this.pushToLog({msg: command, timestamp: Math.floor(Date.now() / 1000), type: 'command'});
    const id = uuid.v4().slice(0, 8);
    await this.serial.write(command, this.version, id, (msg: string) => this.onSuccess(msg), (msg: string) => this.onFailure(msg));
    let maxTimeout = Math.max(this.getBreakTime(), 1000);
    while (!this.written && maxTimeout > 0) {
      maxTimeout -= 1;
      await new Promise((r) => setTimeout(r, 1));
    }
    let timeout = 0;
    switch (command) {
      case 'start_usb\n':
      case 'X\n': timeout = 50;
    }
    if (timeout > 0) {
      await new Promise((r) => setTimeout(r, timeout));
    }
  }

  async getUpdateProgress(): Promise<number> {
    await this.serial.updateProgress((msg: string) => this.progress = Number(msg),
      (msg: string) => {this.progress = -1; this.onFailure(msg); }
    );
    return this.progress;
  }

  getProgress(): number {
    const prog = this.progress;
    return prog;
  }

  async getResult(): Promise<[boolean, string]> {
    let totalTime = 0;
    while (true) {
      await new Promise(resolve => setTimeout(resolve, 10));
      totalTime += 10;
      if (this.isDone) {
        this.isDone = false;
        await new Promise(resolve => setTimeout(resolve, 5));
        // console.log(totalTime);
        return [this.success, this.result];
      }

      const breakTime = this.getBreakTime();
      if (breakTime > 0 && breakTime < totalTime) {
        console.log(`Command ${this.command} - Timeout.`);
        return [false, ''];
      }
    }
  }

}
